import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import { Alert } from '@instructure/ui-alerts';
import { Button } from '@instructure/ui-buttons';
import { Flex } from '@instructure/ui-flex';
import { FormField } from '@instructure/ui-form-field';
import { Heading } from '@instructure/ui-heading';
import { Text } from '@instructure/ui-text';
import { TruncateText } from '@instructure/ui-truncate-text';
import { View } from '@instructure/ui-view';
import formatMessage from 'format-message';
import { useEffect, useRef, useState } from 'react';
import CanvasSelect from '../shared/CanvasSelect';
import { CC_FILE_MAX_BYTES } from '../shared/constants';
import { trackPendoEvent } from '../utils/trackPendoEvent';
import { validateCaptionFile } from './utils/validation';
const LANGUAGE_ERROR_ID = 'cc-language-error';
const FILE_ERROR_ID = 'cc-file-error';
export function ManualCaptionCreator({ languages, onPrimary, onCancel, liveRegion, mountNode, onDirtyStateChanged, elementRef, }) {
    const [selectedLanguageId, setSelectedLanguageId] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [showLanguageError, setShowLanguageError] = useState(false);
    const [fileValidationError, setFileValidationError] = useState('');
    const fileInputRef = useRef(null);
    const languageInputRef = useRef(null);
    const fileButtonRef = useRef(null);
    useEffect(() => {
        languageInputRef.current?.focus();
    }, []);
    const handleLanguageChange = (_event, data) => {
        if (data) {
            setSelectedLanguageId(String(data));
            setShowLanguageError(false);
        }
        onDirtyStateChanged?.(Boolean(data || selectedFile));
    };
    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        onDirtyStateChanged?.(Boolean(file || selectedLanguageId));
        if (!file)
            return;
        const validation = validateCaptionFile(file);
        if (!validation.valid) {
            if (file.size > CC_FILE_MAX_BYTES) {
                trackPendoEvent('canvas_caption_validation_error', {
                    flow_type: 'upload_file',
                    error_type: 'file_too_large',
                });
            }
            setFileValidationError(validation.error || '');
            setSelectedFile(null);
        }
        else {
            setFileValidationError('');
            setSelectedFile(file);
        }
    };
    const handleUploadClick = () => {
        const hasLanguageError = !selectedLanguageId;
        const hasFileError = !selectedFile;
        if (hasLanguageError) {
            trackPendoEvent('canvas_caption_validation_error', {
                flow_type: 'upload_file',
                error_type: 'missing_language',
            });
            setShowLanguageError(true);
        }
        if (hasFileError) {
            trackPendoEvent('canvas_caption_validation_error', {
                flow_type: 'upload_file',
                error_type: 'missing_file',
            });
            setFileValidationError(formatMessage('Please select a file before uploading.'));
        }
        // Focus the first faulty field
        if (hasLanguageError) {
            languageInputRef.current?.focus();
        }
        else if (hasFileError) {
            fileButtonRef.current?.focus();
        }
        // Only proceed if both are selected
        if (selectedLanguageId && selectedFile) {
            onPrimary(selectedLanguageId, selectedFile);
        }
    };
    return (_jsxs(Flex, { as: "div", direction: "column", gap: "medium", elementRef: elementRef, tabIndex: -1, children: [_jsxs(Flex.Item, { overflowY: "hidden", overflowX: "hidden", children: [_jsx(Heading, { as: "h4", variant: "titleCardMini", children: formatMessage('Add New Caption') }), _jsx(Text, { id: "cc-file-hint", variant: "contentSmall", children: formatMessage('Upload a subtitle track in either the SRT or WebVTT format.') })] }), _jsxs(FormField, { id: "cc-language-select", layout: "inline", width: "100%", label: formatMessage('Language*'), children: [_jsx(CanvasSelect, { id: "cc-language-select", inputRef: (el) => {
                            languageInputRef.current = el;
                        }, label: "", placeholder: formatMessage('Select Language'), value: selectedLanguageId, mountNode: mountNode, translatedStrings: {
                            USE_ARROWS: formatMessage('Use arrow keys to navigate options.'),
                            LIST_COLLAPSED: formatMessage('List collapsed.'),
                            LIST_EXPANDED: formatMessage('List expanded.'),
                            OPTION_SELECTED: '{option} selected.',
                        }, onChange: handleLanguageChange, liveRegion: liveRegion, "aria-invalid": showLanguageError ? 'true' : undefined, "aria-describedby": showLanguageError ? LANGUAGE_ERROR_ID : undefined, messages: showLanguageError
                            ? [
                                {
                                    type: 'newError',
                                    text: formatMessage('Please select a language'),
                                },
                            ]
                            : [], children: languages.map(option => (_jsx(CanvasSelect.Option, { id: option.id, value: option.id, children: option.label }, option.id))) }), showLanguageError && (_jsx("span", { id: LANGUAGE_ERROR_ID, children: _jsx(Alert, { variant: "error", screenReaderOnly: true, isLiveRegionAtomic: true, liveRegion: liveRegion, children: formatMessage('Please select a language') }) }))] }), _jsxs(FormField, { id: "cc-file-upload", layout: "inline", width: "100%", label: formatMessage('File*'), inputContainerRef: el => el?.style.setProperty('min-width', '0'), messages: fileValidationError
                    ? [
                        {
                            type: 'newError',
                            text: fileValidationError,
                        },
                    ]
                    : [], children: [_jsxs(Flex, { gap: "space8", alignItems: "center", children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ".vtt, .srt", onChange: handleFileUpload, style: { display: 'none' } }), _jsx(Flex.Item, { shouldShrink: false, children: _jsx(Button, { elementRef: (el) => {
                                        fileButtonRef.current = el;
                                    }, onClick: () => fileInputRef.current?.click(), "aria-describedby": fileValidationError
                                        ? FILE_ERROR_ID
                                        : selectedFile
                                            ? 'cc-file-status'
                                            : 'cc-file-hint cc-file-status', "aria-label": selectedFile
                                        ? formatMessage('Selected file: {name}', { name: selectedFile.name })
                                        : formatMessage('Choose File (required)'), "aria-invalid": fileValidationError ? 'true' : undefined, children: formatMessage('Choose File') }) }), _jsx(View, { id: "cc-file-status", minWidth: 0, role: "status", children: selectedFile ? (_jsx(Text, { variant: "contentSmall", children: _jsx(TruncateText, { children: selectedFile.name }) })) : (_jsx(Text, { variant: "contentSmall", children: formatMessage('No file chosen') })) })] }), fileValidationError && (_jsx("span", { id: FILE_ERROR_ID, children: _jsx(Alert, { variant: "error", screenReaderOnly: true, isLiveRegionAtomic: true, liveRegion: liveRegion, children: fileValidationError }) }))] }), _jsxs(Flex, { gap: "small", children: [_jsx(Button, { color: "secondary", onClick: onCancel, textAlign: "center", width: "auto", children: formatMessage('Cancel') }), _jsx(Button, { color: "primary", onClick: handleUploadClick, textAlign: "center", width: "auto", children: formatMessage('Upload') })] })] }));
}
